import React, { FC, ReactElement, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Modal from 'react-modal';
import { IQuizAnswers } from 'src/stores/reducers/quiz';
import Icon from '../../../Icon';
import { IQuizModalData } from './Quiz';
import './quiz-modal.sass';
import { internalizationState } from '../../../../stores/reducers/sections';
import localeFieldResolver from '../../../../helpers/locale-field-finder';
import cns from 'classnames';
import { quizAnswerRequest, getQuizList } from '../../../../api';
import { getEventKey, getUserEmail, getUserId } from '../../../../stores/reducers/userData';
import { setAndSortQuizList, IQuizItem } from '../../../../stores/reducers/quiz';

Modal.setAppElement('#root');

const QuizModal: FC<IQuizModalData> = ({ isOpen, close, quiz, styles }): ReactElement => {
    const { activeLangState } = useSelector(internalizationState);
    const userEmail = useSelector(getUserEmail);
    const userId = useSelector(getUserId);
    const eventKey = useSelector(getEventKey);

    const [ answerId, setAnswerId ] = useState<string[]>([]);
    const [ answerError, setAnswerError ] = useState<string>('');

    const dispatch = useDispatch();
    
    const handleClose = () => {
        if (close) close();
    };

    useEffect(() => {
        setAnswerId([]);
    }, [quiz]);

    useEffect(() => {
        setAnswerError('');
    }, [answerId]);

    const updateAnswerId = (el: HTMLInputElement) => {
        if (quiz && quiz.multiselect) {
            const temp = Array.from((answerId as string[])).filter((item: string) => item !== el.value);
            if (el.checked) {
                temp.push(el.value);
            }
            setAnswerId(temp);
        } else {
            setAnswerId([el.value]);
        }
    };

    const isVariantActive = (id: string): boolean => {
        return answerId?.indexOf(id) !== -1;
    }

    const renderAnswerVariants = (variant: IQuizAnswers): ReactElement => {
        return (
            <label
                key={variant.id}
                htmlFor={variant.id}
                className={cns('quiz-modal__answer', isVariantActive(variant.id) && 'active')}
                style={{
                    backgroundColor: isVariantActive(variant.id) ? styles?.activeColor : '',
                }}
            >
                <span className="quiz-modal__answer__label">
                    {localeFieldResolver(variant, 'ru', activeLangState)}
                </span>
                <input
                    id={variant.id}
                    name={variant.id}
                    type="checkbox"
                    value={variant.id}
                    hidden
                    checked={isVariantActive(variant.id)}
                    onChange={({target}) => updateAnswerId(target)}
                />
                <span className="quiz-modal__answer__checked">
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.0003 10.3477V11.6399C21.9825 11.7885 21.9637 11.936 21.9465 12.0846C21.7521 13.8974 21.1702 15.575 20.1404 17.0799C18.3741 19.6593 15.9564 21.2627 12.8702 21.8318C12.4637 21.9066 12.0503 21.9459 11.6406 22.0014H10.3529C9.98472 21.9551 9.61489 21.9217 9.24936 21.8598C6.95822 21.4732 4.96263 20.482 3.3191 18.8447C0.843853 16.3735 -0.261342 13.3789 0.0519665 9.89387C0.229714 7.77835 1.05576 5.7692 2.4174 4.14055C4.92441 1.11207 8.15816 -0.274308 12.0837 0.0455C13.8995 0.193559 15.5737 0.813793 17.0783 1.84375C19.6623 3.61077 21.2622 6.03033 21.8307 9.11804C21.9055 9.52453 21.9443 9.93802 22.0003 10.3477ZM9.67841 15.9864C12.341 13.5661 15.0038 11.1446 17.6667 8.72178L15.9306 6.81477L9.74463 12.4383L6.69605 9.38724L4.89049 11.1936L9.67841 15.9864Z" fill="white"/>
                    </svg>
                </span>
            </label>
        );
    };

    const sendAnswer = async () => {
        if (!answerId.length) {
            setAnswerError('Не выбрано ни одного варианта ответа!');
            return;
        }

        const payload = {
            user: userEmail || userId,
            quiz: quiz?.id,
            answer: answerId,
        };

        await quizAnswerRequest(payload);
        const quizResponse: IQuizItem[] = await getQuizList(eventKey, userEmail || userId);
        if (quizResponse.length) {
            dispatch(setAndSortQuizList({ quizList: quizResponse }));
        }

        handleClose();
    };

    return (
        quiz ? 
            <Modal
                className="quiz-modal__content"
                overlayClassName="quiz-modal__overlay"
                isOpen={isOpen}
                onRequestClose={handleClose}
                style={{
                    content: {backgroundColor: styles?.backgroundColor},
                }}
            >
                <button className="modal-close" type="button" onClick={() => handleClose()}>
                    <Icon name="close" />
                </button>
                <div
                    className="quiz-modal__quote"
                    style={{
                        backgroundColor: styles?.buttonColor,
                    }}
                >
                    { activeLangState === 'ru' ? 'Опрос' : 'Quiz' }
                </div>
                <h2
                    className="quiz-modal__question"
                    style={{
                        color: styles?.questionColor,
                    }}
                >
                    {localeFieldResolver(quiz, 'question', activeLangState)}
                </h2>
                {
                    quiz.answerVariants.map((variant) => (renderAnswerVariants(variant)))
                }

                {
                    answerError.length > 0 && 
                    (
                        <div className="quiz-modal__error">
                            {answerError}
                        </div>
                    )
                }
                
                <button
                    className="quiz-modal__submit"
                    style={{
                        backgroundColor: styles?.buttonColor,
                    }}
                    onClick={() => sendAnswer()}
                >
                    { activeLangState === 'ru' ? 'Ответить' : 'Answer' }
                </button>
            </Modal>
        :
            <></>
    );
};

export default QuizModal;
